import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { TextField } from '@carsys/form'
import { Box, Card, CardActionArea, CardContent, CardMedia, Divider, Grid, Skeleton, Stack, Typography } from '@mui/material'
import { Alert, SearchInput } from '@carsys/ui'
import { useFilter } from 'app/hooks/useFilter'
import { FormikProps } from 'formik'
import { FormValues } from './CaptureGMSForm'
import KeyloopLogo from 'assets/img/Keyloop-Logo.png'
import KeyloopLogin from './KeyloopLogin'
import { useFeature } from '@cbgms/base/feature-flags'
import ConfirmCloseKeyloopModal from './ConfirmCloseKeyloopModal'
import { deleteKeyloopConnection } from 'app/auth/state/actions'
import { useGetCurrentShopID } from 'app/shops/hooks/useGetCurrentShopID'
import { IRootReducerState } from 'state/rootReducer'
import { useNotifier } from '@cbgms/base/notifier'
import { useOrderAccountsContext } from 'app/workorder/contexts/use-order-account-context'
import { ProductIDType } from '@cbgms/api'
interface Props<T> {
  formikProps: FormikProps<FormValues>
  externalGMSList: T[]
  isLoading: boolean
}

export const CaptureGMSFields = <T extends { UUID: string; ImageURL: string; Name: string; HasRemark: boolean }>({
  formikProps,
  externalGMSList,
  isLoading
}: React.PropsWithChildren<Props<T>>) => {
  const companyUuid = useSelector((state: IRootReducerState) => state.auth?.user?.IdToken['custom:company_uuid'])
  const shopID = useGetCurrentShopID()
  const { selectedOrderAccount } = useOrderAccountsContext()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const notifier = useNotifier()
  const hasAxnlkeyloopFeature = useFeature('axnl-keyloop')
  const [openKeyloopLogin, setOpenKeyloopLogin] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const GMSListItems =
    externalGMSList.map(gms => ({
      UUID: gms.UUID,
      LogoURL: gms.ImageURL,
      Name: gms.Name,
      HasRemark: gms.HasRemark
    })) || []

  const [filteredGMSList, setFilter, filter] = useFilter(GMSListItems)

  const handleOpenKeyloop = useCallback(
    (open: boolean) => {
      setOpenKeyloopLogin(open)
    },
    [open]
  )

  const handleRemoveConnection = async () => {
    try {
      const { Data }: { Data: unknown } = await dispatch(deleteKeyloopConnection(companyUuid, shopID))
      const data = Data as { Status: string; Message: string }
      if (data.Status === 'Success') notifier.success(t('onboarding.capture_gms_information.delete_keyloop_success'))
    } catch (err) {
      notifier.error(t('onboarding.capture_gms_information.failed_to_delete_keyloop'))
    } finally {
      setShowModal(false)
      handleOpenKeyloop(false)
    }
  }

  return (
    <Grid container direction='column' spacing={2} sx={{ mb: 1 }}>
      {!!formikProps.values.HasRemark && (
        <>
          <Grid item>
            <Typography variant='h5'>{t('onboarding.capture_gms_information.other_system_field_title')}</Typography>
          </Grid>
          <Grid item>
            <TextField name='Name' label={t('onboarding.capture_gms_information.other_system_field_label')} />
          </Grid>
          <Grid item>
            <Divider />
          </Grid>
        </>
      )}
      <Grid item>
        <SearchInput
          fullWidth
          variant='outlined'
          onChange={e => setFilter(e.target.value)}
          value={filter}
          placeholder={t('global.search')}
        />
      </Grid>
      <Grid item>
        {isLoading ? (
          <LoadingSkeleton />
        ) : (
          <Grid container spacing={2} data-testid='select-card-grid' paddingBottom={1}>
            {filteredGMSList.map((item, index: number) => {
              const isSelected = formikProps.values.UUID === item.UUID
              return (
                <Grid item md={4} xs={4} key={index} display='flex' flexDirection='column'>
                  <Card
                    sx={theme => ({
                      flex: 1,
                      borderStyle: 'solid',
                      borderColor: theme.palette.primary.light,
                      borderWidth: isSelected ? '1px' : 0,
                      backgroundColor: isSelected ? theme.palette.action.selected : 'inherit'
                    })}
                    raised={isSelected}
                    data-testid='select-card-item'
                    onClick={() => {
                      formikProps.resetForm() // clear field values
                      formikProps.setFieldValue('UUID', item.UUID)
                      formikProps.setFieldValue('HasRemark', item.HasRemark)
                    }}
                  >
                    <CardActionArea sx={{ height: '100%' }}>
                      <CardContent>
                        <Stack direction='row' spacing={2} justifyContent='space-between'>
                          <Box alignSelf='center'>
                            <Typography component='div' variant='inherit'>
                              {item.Name}
                            </Typography>
                          </Box>
                          {item.LogoURL && (
                            <Box sx={{ height: '4em' }}>
                              <CardMedia
                                component='img'
                                sx={{ height: '100%', objectFit: 'contain', objectPosition: 'right' }}
                                image={item.LogoURL}
                                alt='logo'
                                data-testid='card-item-logo'
                              />
                            </Box>
                          )}
                        </Stack>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              )
            })}
            {filteredGMSList.length === 0 && (
              <Grid item xs={12}>
                <Alert color='muted'>{t('global.search_no_results_found')}</Alert>
              </Grid>
            )}
          </Grid>
        )}
      </Grid>
      {hasAxnlkeyloopFeature && selectedOrderAccount?.Wholesaler?.OrderSystem === ProductIDType.VPOMAxNl.toString() ? (
        <>
          <Grid item>
            <Typography variant='h5'>{t('onboarding.capture_gms_information.keyloop_field_title')}</Typography>
            <Grid container spacing={2} data-testid='select-card-grid' paddingBottom={1}>
              <Grid item md={4} xs={4} key={1} display='flex' flexDirection='column'>
                <Card
                  sx={theme => ({
                    flex: 1,
                    borderStyle: 'solid',
                    borderColor: theme.palette.primary.light,
                    borderWidth: 0,
                    backgroundColor: 'inherit'
                  })}
                  data-testid='select-card-item1'
                  onClick={() => setOpenKeyloopLogin(true)}
                >
                  <CardActionArea sx={{ height: '100%' }}>
                    <CardContent>
                      <Stack direction='row' spacing={2} justifyContent='space-between'>
                        <Box alignSelf='center'>
                          <Typography component='div' variant='inherit'>
                            {t('onboarding.capture_gms_information.keyloop')}
                          </Typography>
                        </Box>
                        <Box sx={{ height: '4em' }}>
                          <CardMedia
                            component='img'
                            sx={{ height: '100%', objectFit: 'contain', objectPosition: 'right' }}
                            image={KeyloopLogo}
                            alt='logo'
                            data-testid='card-item-logo'
                          />
                        </Box>
                      </Stack>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            </Grid>
          </Grid>
          <KeyloopLogin open={openKeyloopLogin} onCancel={() => handleOpenKeyloop(false)} onReset={() => setShowModal(true)} />
          {showModal ? (
            <ConfirmCloseKeyloopModal
              title={t('AppLabels.confirm')}
              content={t('onboarding.capture_gms_information.keyloop_remove_confirm')}
              closeModal={() => setShowModal(false)}
              onConfirmButtonClick={handleRemoveConnection}
            />
          ) : null}
        </>
      ) : null}
    </Grid>
  )
}

function LoadingSkeleton() {
  return (
    <Grid container spacing={2}>
      <ItemSkeleton />
      <ItemSkeleton />
      <ItemSkeleton />
      <ItemSkeleton />
      <ItemSkeleton />
      <ItemSkeleton />
    </Grid>
  )
}

function ItemSkeleton() {
  return (
    <Grid item md={4}>
      <Skeleton animation='wave' />
    </Grid>
  )
}
